import { AlertColor as AlertSeverity } from '@mui/lab';
import {
  // makeStyles,
  Tooltip,
} from '@mui/material';
import {
  GridRenderCellParams,
  GridRowData,
  GridSortModel,
} from '@mui/x-data-grid';
import { AxiosError, AxiosResponse, CancelTokenSource } from 'axios';
import { History } from 'history';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { Link, Route, Switch } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import { useBusinessPermissions } from '../../../../common/helperFunctions/useBusinessPermissions';
import { Logger } from '../../../../common/Logger';
import { trackAddWebhook } from '../../../../common/userTracking';
import { endingDate } from '../../../../recoil/atoms/endingDate';
import { startingDate } from '../../../../recoil/atoms/startingDate';
import { IUserAtom, user } from '../../../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../../services/rest/makeAPIRequest';
import { useSnackbarWithStyles } from '../../../components/notifications/SnackbarProviderWithStyles';
import { QueuesLoadingDialog } from '../../../modules/Manage/pages/queues-loading-dialog/QueuesLoadingDialog';
import { ActionColumn } from '../../../modules/Manage/pages/queues-table/ActionColumns';
import dataTableStyles from '../../../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../../../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../../../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { ErrorOnValidate } from '../../../pages/ManageCadences2';

import { WebhookAddDialog } from './WebhookAddDialog';
import { WebhooksDeleteDialog } from './WebhooksDeleteDialog';
export interface IWebhook {
  batchdelay: number;
  businessid: number;
  callresult: string;
  direction: string;
  dispositions: string | null;
  contactManagement: string | null;
  event: string;
  id: number;
  isCustomCRM: 0 | 1;
  name: string;
  runtime: string;
  webhookLocation: string;
  webhookid: number;
  headers: any;
}

// const useStyles = makeStyles({
//   disposition: {
//     width: 253,
//   },
// });

/**
 * Show DataGrid table with controls to show modals (with dedicated routes)
 */
export function Webhooks(props: {
  /** React Router history used to redirect to modal, etc. */
  history: History;
}) {
  // const classes = useStyles();
  // const classes = useStyles();
  const eventNameMapping = {
    endcall: 'End Call',
    startcall: 'Start Call',
    answeredcall: 'Answered Call',
    sms: 'SMS',
    disposition: 'Call Outcome',
    voicemail: 'Voicemail',
    scheduledactivity: 'Scheduled Activity',
    cisummary: 'CI Summary',
    powerdialattempt: 'PowerDial Attempts',
    powerlistsession: 'Agent PowerDial Session',
  };
  const start = useRecoilValueLoadable(startingDate);
  const end = useRecoilValueLoadable(endingDate);
  const [selected, setSelected] = useState<GridRowData[]>([]);
  const [deleteItemId, setDeleteItemId] = useState<
    string | number | undefined
  >();
  const [deleteCancelTokenSource, setDeleteCancelTokenSource] = useState<
    CancelTokenSource | undefined
  >();

  // get atoms and selectors
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;

  const setRefetchToggleForWebhooks = useSetRecoilState(
    refetchToggle(endpointEnum.GET_WEBHOOKS)
  );

  const mutation = useMutation(
    getApiMutation(userAtomContents, setUserAtom, setDeleteCancelTokenSource)
  );
  // notification using snackbar
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  /**
   * Transform by adding id
   * NOTE: must use useMemo so rows do not get updated on re-render
   */
  const [webhooksCached, webhooksLoadable] = makeApiRequestCached<
    IWebhook[] | undefined
  >(
    {
      method: 'POST',
      endpoint: endpointEnum.GET_WEBHOOKS,
      payloadJsonString: JSON.stringify({
        businessid: userAtomContents.businessid,
        apikey: userAtomContents.apikey,
        call: methodNameEnum.GET_WEBHOOKS,
      }),
    },
    (loadable, cached) =>
      loadable.state !== 'loading'
        ? loadable.contents?.response?.data?.result
            ?.filter((webhook: IWebhook) => !webhook.isCustomCRM)
            .map((row: any) => ({
              ...row,
              id: row.webhookid, // allow number sorting
            }))
        : cached.current
  );

  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const dispositionsLoadable = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_USER_DISPOSITION_REPORTING,
      payloadJsonString: JSON.stringify({
        startdate: start.contents,
        enddate: end.contents,
        timezone:
          businessData?.contents?.response?.data?.result?.[0]?.timezone_php,
      }),
      waitForPayload: !businessData?.contents?.response?.data?.result?.[0]
        ?.timezone_php,
    })
  );
  const disposCached = useRef<any[]>([]);
  disposCached.current = useMemo(
    () =>
      dispositionsLoadable?.contents?.data?.dispositions?.result
        ? dispositionsLoadable?.contents?.data?.dispositions?.result?.map(
            (o: any) => ({
              ...o,
              id: o.businessDispositionid,
            })
          )
        : disposCached.current,
    [dispositionsLoadable]
  );
  /**
   * Callback used by column button to delete queue
   * @param id Queue to be deleted
   */
  const handleDeleteWebhook = async (id: string | number) => {
    let message: string | React.ReactNode = 'Update Successful'; // ! to ignore usage before assignment error
    let severity: AlertSeverity = 'success'; // ! to ignore usage before assignment error
    const data = {
      call: methodNameEnum.DELETE_WEBHOOK,
      apikey: userAtomContents.apikey,
      businessid: userAtomContents.businessid,
      webhookid: id,
    };
    let response!: AxiosResponse<any>;
    let exception!: AxiosError | Error;
    try {
      response = await mutation.mutateAsync({
        endpoint: endpointEnum.DELETE_WEBHOOK,
        payload: data,
      });

      // clear selections list
      setDeleteItemId(undefined);
      if (response.data.success === false) {
        if (response.data.errorOnValidate) {
          const errorOnValidate = response.data
            .errorOnValidate as ErrorOnValidate;
          throw Object.assign(
            new ErrorOnValidate(errorOnValidate.message),
            errorOnValidate
          );
        }

        throw new Error(response.data.message ?? 'Update failed');
      }
    } catch (ex) {
      exception = ex as AxiosError | Error;

      message = 'Failed to remove webhook';
      severity = 'error';

      if ((ex as AxiosError).isAxiosError) {
        const axiosError = ex as AxiosError;
        message = axiosError.message;
      }
    }

    // log success
    Logger.trace(
      {
        message,
        response,
        exception,
      },
      'WEBHOOK:DELETEWEBHOOK'
    );

    enqueueSnackbarWithStyles({
      message,
      alertProps: {
        severity,
      },
    });
    setRefetchToggleForWebhooks({});
  };

  function nameFormatter(params: GridRenderCellParams) {
    return (
      <Tooltip title="Edit">
        <Link to={`/manage/webhooks/${params.row.id}/edit`}>
          {params.value}
        </Link>
      </Tooltip>
    );
  }
  function linkFormatter(params: GridRenderCellParams) {
    return (
      <Tooltip title="Go to URL">
        <a href={`${params.value}`} target="_blank" rel="noreferrer">
          {params.value}
        </a>
      </Tooltip>
    );
  }
  const readOnly = useBusinessPermissions().accountReadOnly;
  /**
   * Create column for DataGrid renderer
   * use useMemo to prevent unneeded rerenderings
   */
  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 70,
      },
      {
        field: 'name',
        headerName: 'Name',
        headerClassName: dataTableStyles.fullWidth,
        cellClassName: dataTableStyles.fullWidth,
        renderCell: nameFormatter,
      },
      {
        field: 'event',
        headerName: 'Event',
        valueFormatter: (params: GridRenderCellParams) =>
          eventNameMapping[`${params.value}`],
        width: 150,
      },
      {
        field: 'runtime',
        headerName: 'Runtime',
      },
      {
        field: 'direction',
        headerName: 'Direction',
      },
      {
        field: 'callresult',
        headerName: 'Call Result',
      },
      {
        field: 'dispositions',
        headerName: 'Call Outcome(s)',
        headerClassName: dataTableStyles.fullWidth,
        cellClassName: dataTableStyles.fullWidth,
        renderCell: (params: GridRenderCellParams) => (
          <>{params.value?.toString().split(',').join(', ')}</>
        ),
      },
      {
        field: 'contactManagement',
        headerName: 'Contact Management',
        headerClassName: dataTableStyles.fullWidth,
        cellClassName: dataTableStyles.fullWidth,
        renderCell: (params: GridRenderCellParams) => (
          <>{params.value?.toString().split(',').join(', ')}</>
        ),
      },
      {
        field: 'webhookLocation',
        headerName: 'URL',
        headerAlign: 'center',
        width: 125,
        headerClassName: dataTableStyles.fullWidth,
        cellClassName: dataTableStyles.fullWidth,
        renderCell: linkFormatter,
      },
      {
        field: 'action',
        headerName: 'Action',
        headerAlign: 'center',
        width: 150,
        hide: readOnly,
        disableSearch: true, // do not search field content
        disableRowSelection: true, // to have onlcick to work row selection should be disabled
        enableSorting: false, // do not sort by action
        renderCell: ActionColumn({
          deleteItemId,
          setDeleteItemId,
          isLoading: mutation.isLoading,
          handleDeletion: handleDeleteWebhook,
          editLink: '/manage/webhooks/:id/edit',
          deleteCancelTokenSource,
          noLeftIconMargin: true,
        }),
      },
    ],
    [deleteItemId, mutation.isLoading]
  ) as IColDef[];

  /**
   * Create cutom toolbar component for table
   * Use useCallback to stop rerendering
   */
  const ToolbarComponent = useCallback(
    (toolProps: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...toolProps}
        dataType="Webhooks"
        numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            numSelected={selected.length}
            onClickForAdd={() => {
              trackAddWebhook();
              props.history.push('/manage/webhooks/new');
            }}
            onClickForDelete={() => {
              props.history.push('/manage/webhooks/delete');
            }}
            isLoading={toolProps.isLoading}
          />
        }
      />
    ),
    [selected.length]
  );
  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'id', sort: 'asc' },
  ]);
  return (
    <>
      <QueuesLoadingDialog />
      <Switch>
        <Route path="/manage/webhooks/new">
          {({ history, match }) => (
            <WebhookAddDialog
              show={match != null}
              onHide={() => {
                history.push('/manage/webhooks');
              }}
            />
          )}
        </Route>
        <Route path="/manage/webhooks/:id/edit">
          {({ history, match }) => (
            <WebhookAddDialog
              show={match != null}
              id={match?.params.id}
              onHide={() => {
                history.push('/manage/webhooks');
              }}
              webhook={webhooksCached.current?.find(
                (webhook) => webhook.id == match?.params.id
              )}
            />
          )}
        </Route>
        <Route path="/manage/webhooks/delete">
          {({ history, match }) => (
            <WebhooksDeleteDialog
              selected={selected}
              setSelected={setSelected}
              show={match != null}
              onHide={() => {
                history.push('/manage/webhooks');
              }}
            />
          )}
        </Route>
      </Switch>
      <DataTable
        enableSelectAll={true}
        rows={webhooksCached.current ?? []}
        columns={columns}
        loading={webhooksLoadable.state === 'loading'}
        ToolbarComponent={ToolbarComponent}
        sortModel={sortModel}
        setGridSortModel={setGridSortModel}
        selected={selected}
      />
    </>
  );
}
